
import { BehaviorSubject } from 'rxjs';
import { filter } from 'rxjs/operators';
import { IKWFHubClient, IKWFServerState, IKWFClientState, IKWFHub } from './rwfsignalrtypes';
import { CompatibilityService } from './services/compatibilityservice';
import { IWebApiRoot } from './navdata';

/** Base 'angular(js)' unaware SignalR Communication object */
export class RwfSignalRService {

    /** Callbacks Observable */
    public readonly Callbacks = new BehaviorSubject<{ Event: keyof IKWFHubClient, Arguments: any[] }>(undefined);

    /** Current Application State */
    public readonly ApplicationState = new BehaviorSubject<IKWFServerState & IKWFClientState>(<any><Partial<IKWFClientState>>{

    });
    private _SignalR: SignalR;
    private _LoadScript: PromiseLike<any>;

    public readonly ConnectionFailure = new BehaviorSubject<boolean>(false);

    constructor(
        private readonly $c: CompatibilityService,
        /** Url to rwf */
        public readonly rwfUrl: string | IWebApiRoot | PromiseLike<string | IWebApiRoot> = undefined,
    ) {
    }

    /** Register for Callback notification for given eventName */
    public Register<TKey extends keyof IKWFHubClient>(eventName: TKey, callback: IKWFHubClient[TKey]) {
        const r = this.Callbacks.pipe(filter(x => x && eventName === x.Event)).subscribe(s => (<Function>callback).apply(undefined, s.Arguments));
        //Implicitly Ensure Connection (Really here(?))
        this.getKwfHub();
        return r;
    }

    /** Get (imp Create) KWF Hub, (so far destruction only on WindowUnload) */
    public getKwfHub(): PromiseLike<IKWFHub> {
        const ConnectionFailure = this.ConnectionFailure;
        const $c = this.$c;
        let result = this._getKWfHub;
        if (result) {
            // After Successfull Connection : force reconnection if disconnected
            const reHub = this._SignalR && this._SignalR.hub;
            if (reHub) {
                return result.then(r => {
                    if (reHub.state === this._SignalR.connectionState.disconnected) {
                        ConnectionFailure.next(true);
                        return $c.when(reHub.start()).then(() => {
                            ConnectionFailure.next(false);
                            return result;
                        });
                    } else {
                        return result;
                    }
                });
            }
            return result;
        }

        if (!result) {
            return result = this._getKWfHub = this.getSignalR().then(signalR => {
                const kwfHub = <IKWFHub>signalR['kWFHub'];
                if (!kwfHub) {
                    return $c.reject<IKWFHub>('Hub "kwfHub" not found');
                }
                const clientInfo: Record<keyof IKWFHubClient, any> = {
                    onClientChange: 0,
                    onDeleteNotification: 0,
                    onEditChange: 0,
                    onEditedInstanceChanged: 0,
                    onForcedOpen: 0,
                    onPushNotification: 0,
                    onRequestClientState: 0,
                    onNavUiClosed: 0,
                    onRegisteredAsExternalSlave: 0,
                    onNotifyExternalSlaveRegistrationRequest: 0,
                    onNotifyExternalSlaveUnregistrationRequest: 0,
                    onNotifyExternalSlave: 0
                };

                Object.getOwnPropertyNames(clientInfo).forEach((clientName: keyof IKWFHubClient) => {
                    kwfHub.client[clientName] = (...args: any[]) => {
                        const _result = this.Callbacks.next({ Event: clientName, Arguments: args });
                        this.$c.tick();
                        return _result;
                    };
                });

                const hub = signalR.hub;

                return $c.when(hub.start({ jsonp: true })).then(() => {
                    hub.disconnected(() => {
                        console.log("SignalR Disconnected");
                        // TODO: Mark Disconnected ?
                    });
                    hub.reconnected(() => this.reloadApplicationState(kwfHub));
                    return this.reloadApplicationState(kwfHub).then(() => kwfHub);
                }, err => {
                    ConnectionFailure.next(true);
                    throw err;
                });
            });
        }
    }
    // tslint:disable-next-line:member-ordering
    private _getKWfHub: PromiseLike<IKWFHub>;

    public releaseKWFHub() {
        const connection = $.connection;
        if (connection && connection.hub) {
            connection.hub.stop(false, true);
        }
    }

    private getSignalR(): PromiseLike<SignalR> {
        if (this._SignalR) {
            return this.$c.when(this._SignalR);
        }

        if (!this._LoadScript) {
            this._LoadScript = this.getRwfUrl().then(rwfUrl => {
                const prefix = rwfUrl.replace(/\/+$/, '') || '.';
                return this.$c.when($.getScript(prefix + '/signalr/hubs'));
            });
        }

        return this._LoadScript.then(() => {
            if (this._SignalR) {
                return this._SignalR;
            }

            const signalr = $.connection;
            if (!signalr) {
                return this.$c.reject<SignalR>('SignalR not available');
            }
            return this.getRwfUrl().then(rwfUrl => {
                const prefix = rwfUrl.replace(/\/+$/, '') || '.';
                signalr.hub.url = prefix + '/signalr';
                return signalr;
            });
        });
    }

    private reloadApplicationState(kwfHub: IKWFHub): PromiseLike<any> {

        const thisM: this & { __Reload?: PromiseLike<any> } = <any>this;
        return thisM.__Reload || (thisM.__Reload = this.$c.when(
            kwfHub.server.getApplicationState(this.ApplicationState.getValue())).then(state => {
                const value = this.ApplicationState.getValue();
                Object.getOwnPropertyNames(state).forEach(v => {
                    value[v] = state[v];
                });
                if (!value.SessionId) {
                    const err = 'Ungültige Information vom Server: Keine Information zur aktuellen SessionId';
                    this.ApplicationState.error(err);
                    throw new Error(err);
                }

                this.ApplicationState.next(value);
                return kwfHub;
            }).then(() => {
                delete thisM.__Reload;
                return kwfHub;
            }, (err) => { delete thisM.__Reload; throw err; }));
    }

    private getRwfUrl() {
        return this.$c.when(this.rwfUrl).then(rwfUrl => {
            if (!(typeof rwfUrl === "string")) {
                rwfUrl = (rwfUrl && rwfUrl.WebApiRoot) || "";
            }
            return <string>rwfUrl || "";
        });
    }
}
